import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import T from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import AppQuickView from './AppQuickView';
import PubQuickview from './PubQuickView';
import CompanyQuickView from './CompanyQuickView';
import Animation from '../Animation';
import SideModalContainer from '../Modals/SideModalContainer';

import { closeQuickview, saveScrollPosition } from './actions';
import { isQuickViewOpenSelector, activeEntitySelector } from './selectors';
import { QV_TYPE } from './configs/constants';
import { getQVTypeByKind } from './utils';
import scrollPage from '../../utils/scrollPage';
import { useOnClickOutside } from '../../hooks';

const QuickView = ({ enableCloseOutside = true }) => {
  const dispatch = useDispatch();

  const isOpen = useSelector(isQuickViewOpenSelector);
  const activeEntity = useSelector(activeEntitySelector);

  const kind = getQVTypeByKind(activeEntity?.kind);
  const isAppQV = kind === QV_TYPE.app;
  const isPubQV = kind === QV_TYPE.publisher;
  const isCompanyQV = kind === QV_TYPE.company;

  const containerRef = useRef();
  const scrollRef = useRef();

  const saveQVScrollPosition = useCallback(() => {
    dispatch(saveScrollPosition(scrollRef.current.scrollTop));
  }, []);

  const handleScroll = useCallback(({ target }) => {
    const offsetTop = 60;
    scrollRef.current.classList[target.scrollTop > offsetTop ? 'add' : 'remove']('shadow');
  }, []);

  const handleClose = useCallback(() => {
    dispatch(closeQuickview());
  }, []);

  useOnClickOutside(containerRef, handleClose, { enabled: enableCloseOutside && isOpen });

  // Adjust scrolling when switching QV
  useEffect(() => {
    if (kind && scrollRef.current) {
      scrollPage({
        destination: activeEntity.scrollPosition || 0,
        scrollableArea: scrollRef.current,
      });
    }
  }, [kind]);

  return createPortal(
    <Animation show={isOpen} animationType="slide-fade-in-out-right">
      <SideModalContainer
        ref={containerRef}
        contentContainerRef={scrollRef}
        className="entity-quick-view-sidebar"
        onScroll={handleScroll}
      >
        {isAppQV && <AppQuickView onClose={handleClose} />}
        {isPubQV && (
          <PubQuickview saveQVScrollPosition={saveQVScrollPosition} onClose={handleClose} />
        )}
        {isCompanyQV && <CompanyQuickView onClose={handleClose} />}
      </SideModalContainer>
    </Animation>,
    document.body
  );
};

QuickView.propTypes = {
  enableCloseOutside: T.bool,
};

export default QuickView;
